import React, { Component } from 'react';
import styled from 'styled-components';
import { changeLocation } from 'api/Location';
import { getRequest, batchRequest, postRequest } from '../../api/ApiService';
import { fetchTracks, invalidateTracks } from '../../services/tracks-cache';
import { Loader } from 'react-loaders';
import Auth from '../../routes/Auth';
import orderBy from 'lodash/orderBy';

import Pagination from './Pagination';
import Page404 from './Page404';

import TriviaIntro from './trivia/Intro';
import TriviaSingleChoice from './trivia/SingleChoice';
import TriviaMultipleChoice from './trivia/MultipleChoice';
import TriviaFormula1 from './trivia/Formula1';
import TriviaFormula2 from './trivia/Formula2';
import TriviaFinal from './trivia/Final';

import ConfettiExplosion from './ConfettiExplosion';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: stretch;

  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const LoaderWrapper = styled.div`
  background: #f5f6f7;
  padding: 40px;
  flex: 1;
`;

const ConfettiWrapper = styled.div`
  position: absolute;
  top: 59px;
  left: 210px;
  width: calc(100vw - 210px);
  height: calc(100vh - 139px);
  overflow: hidden;
`;

const ConfettiPosition = styled.div`
  position: absolute;
  top: 30%;
  left: 50%;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: calc(30% + 59px);
  left: calc(50%);

  font-family: 'Mukta Vaani';
  font-style: normal;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  padding: 10px 20px;
  background: #FFBABA;
  border: 1px solid #D8000C;
  border-radius: 8px;
  z-index: 3000;
`;

class RoundTriviaPage extends Component {

  constructor(props) {
    super(props);

    const activeRound = Number(props.roundInUrl || props.currentRound);
    this.state = {
      loading: false,
      pages: [
        {
          title: `Trivia: Round ${activeRound}`,
          alias: 'trivia',
          steps: [
            { component: TriviaIntro, alias: 'intro' },
          ],
        },
      ],
      currentPage: -2,
      currentStep: -2,
      mode: 'basic', // basic | disabled | validate | loading
      validateCallback: null,
      history: [],
      showConfetti: false,
      isFetchData: false,
      showIncorrectMessage: false,
    };
  }

  componentDidMount() {
    this.loadNavigation(() => {
      this.getCurrentPage();
    });
  }

  setFetchData = (isFetchData) => {
    this.setState({
      isFetchData,
    });
  }

  loadNavigation = (callback) => {
    const activeRound = this.activeRound;

    batchRequest([
      `${process.env.REACT_APP_API_URL}v2/trivia/coursetrivias/`,
      `${process.env.REACT_APP_API_URL}static/debt_clients/`,
    ])
      .then(res => {
        const userId = Number(Auth.getUserId());
        const triviasRaw = res[0].body;
        const stage = triviasRaw.results.find(i => i.trivia.stage === `${activeRound}`);
        const triviaId = stage ? stage.id : 0;
        let questions = stage ? stage.trivia.triviaquestions : [];
        questions = orderBy(questions, ['list_order'], ['asc']);
        for (let i=0; i<questions.length; i++) {
          questions[i].question.optionchoices = orderBy(questions[i].question.optionchoices, ['id'], ['asc']);
        }

        const debtClients = res[1].body;
        const debtClient1 = debtClients.find(i => i.id === 1);
        const debtClientValue1 = debtClient1 ? `${(Number(debtClient1.lt_debt_to_capital) * 100).toFixed(0)}%` : '';
        const debtClient2 = debtClients.find(i => i.id === 2);
        const debtClientValue2 = debtClient2 ? `${(Number(debtClient2.lt_debt_to_capital) * 100).toFixed(0)}%` : '';
        const debtClient3 = debtClients.find(i => i.id === 3);
        const debtClientValue3 = debtClient3 ? `${(Number(debtClient3.lt_debt_to_capital) * 100).toFixed(0)}%` : '';

        const triviaSteps = [];
        let counter = 0;
        for (const question of questions) {
          counter++;
          if (question.question.question_type === 'Single Choice') {
            triviaSteps.push({
              component: TriviaSingleChoice,
              alias: `question${counter}`,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          } else if (question.question.question_type === 'Multiple Choice') {
            triviaSteps.push({
              component: TriviaMultipleChoice,
              alias: `question${counter}`,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          } else if (
            question.question.question_type === 'Multiple Choice with Ordering'
            && [22, 38, 15].includes(question.question.id)
          ) {
            triviaSteps.push({
              component: TriviaFormula1,
              alias: `question${counter}`,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                captions: question.question.id === 22
                  ? ['Assets', 'Liabilities', 'Equity']
                  : question.question.id === 38
                  ? ['Gross Profit Section', 'Operating Profit Section', 'Net Profit Section']
                  : [debtClientValue1, debtClientValue2, debtClientValue3],
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          } else if (
            question.question.question_type === 'Multiple Choice with Ordering'
            && [20, 37, 35, 36, 34, 28].includes(question.question.id)
          ) {
            triviaSteps.push({
              component: TriviaFormula2,
              alias: `question${counter}`,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                caption: question.question.id === 20
                  ? 'Market Share'
                  : [37, 35].includes(question.question.id)
                  ? 'Return on Assets'
                  : [36, 34].includes(question.question.id)
                  ? 'Return on Equity'
                  : 'Debt to Total Capital',
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          }
        }

        this.setState({
          pages: [
            {
              title: `Trivia: Round ${activeRound}`,
              alias: 'trivia',
              steps: [
                { component: TriviaIntro, alias: 'intro' },
                ...triviaSteps,
                { component: TriviaFinal, alias: 'done' },
              ],
            },
          ],
        }, () => {
          if (callback) {
            callback();
          }
        });
      })
      .catch(err => {});
  }

  componentDidUpdate() {
    this.getCurrentPage();
  }

  showIncorrectMessage = () => {
    this.setState({
      showIncorrectMessage: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showIncorrectMessage: false,
        })
      }, 5000);
    });
  }

  getLastNotFinishedPage = (trackedPages, pages) => {
    let prevPage = 0;
    let prevStep = 0;
    for (let pageNum=0; pageNum<pages.length; pageNum++) {
      for (let stepNum=0; stepNum<pages[pageNum].steps.length; stepNum++) {
        const pageAlias = pages[pageNum].alias;
        const stepAlias = pages[pageNum].steps[stepNum].alias;

        const foundPage = trackedPages.find(i => i[0] === pageAlias && i[1] === stepAlias);
        if (!foundPage) {
          return {
            page: prevPage,
            step: prevStep,
          };
        }

        prevPage = pageNum;
        prevStep = stepNum;
      }
    }

    return {
      page: prevPage,
      step: prevStep,
    };
  }

  get activeRound() {
    const { currentRound, roundInUrl } = this.props;

    return roundInUrl || currentRound;
  }

  getCurrentPage() {
    const { location } = this.props;
    const { pages } = this.state;
    const currentRound = this.activeRound;

    const pathes = location.pathname.substring(1).split('/');

    let currentPage = 0;
    let currentStep = 0;
    let doRedirect = true;
    if (pathes.length > 1) {
      currentPage = -1;
      currentStep = -1;
      doRedirect = false;

      const category = pages.findIndex(i => i.alias === pathes[1]);
      if (category > -1) {
        if (pathes.length === 3) {
          const step = pages[category].steps.findIndex(i => i.alias === pathes[2]);
          if (step > -1) {
            currentPage = category;
            currentStep = step;
          }
        } else {
          currentPage = category;
          currentStep = 0;
        }
      }

      if (
        currentPage !== this.state.currentPage ||
        currentStep !== this.state.currentStep
      ) {
        this.setState({
          loading: true,
          currentPage,
          currentStep,
        });

        const user_id = Number(Auth.getUserId());
        const round_number = Number(currentRound);
        const game_id = Number(Auth.getGameId());
        const course_id = Number(Auth.getCourseId());
        fetchTracks({ gameId: game_id, courseId: course_id, userId: user_id, roundNumber: round_number })
          .then(res => {
            let history = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/round-trivia/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/round-trivia/`, ''));

            this.setState({
              loading: false,
              history,
            }, () => {
              if (doRedirect) {
                const page = pages[currentPage].alias;
                const step = pages[currentPage].steps[currentStep].alias;

                changeLocation(
                  `/round-trivia/${page}${step !== '' ? '/' + step : ''}?round=${currentRound}`
                );
              }
            });
          })
          .catch(err => {});
      }
    } else {
      if (this.state.loading !== true) {
        this.setState({
          loading: true,
        });

        const user_id = Number(Auth.getUserId());
        const round_number = Number(currentRound);
        const game_id = Number(Auth.getGameId());
        const course_id = Number(Auth.getCourseId());
        fetchTracks({ gameId: game_id, courseId: course_id, userId: user_id, roundNumber: round_number })
          .then(res => {
            let history = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/round-trivia/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/round-trivia/`, ''));
            const trackedPages = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/round-trivia/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/round-trivia/`, '').split('/'))
              .map(i => i.length === 2 ? i : [i[0], '']);

            if (trackedPages.length > 0) {
              const foundPage = this.getLastNotFinishedPage(trackedPages, pages);
              if (foundPage) {
                currentPage = foundPage.page;
                currentStep = foundPage.step;
              }
            }

            if (doRedirect) {
              const page = pages[currentPage].alias;
              const step = pages[currentPage].steps[currentStep].alias;

              if (currentPage === 0 && currentStep === 0 && !history.includes(`${page}${step !== '' ? '/' + step : ''}`)) {
                postRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/`)
                  .send({
                    page: `${window.location.protocol}//${window.location.host}/round-trivia/${page}${step !== '' ? '/' + step : ''}`,
                    user: Auth.getUserId(),
                    course: Auth.getCourseId(),
                    game: Auth.getGameId(),
                    round_number: currentRound,
                  })
                  .then((data) => {
                    invalidateTracks();
                    history = [
                      ...history,
                      `${page}${step !== '' ? '/' + step : ''}`,
                    ];

                    this.setState({
                      history,
                    });
                  });
              }

              changeLocation(
                `/round-trivia/${page}${step !== '' ? '/' + step : ''}?round=${currentRound}`
              );
            }

            this.setState({
              loading: false,
              currentPage,
              currentStep,
              history,
            });
          })
          .catch(err => {});
      }
    }
  }

  trackPage = (url, page, step) => {
    const currentRound = this.activeRound;

    const user_id = Number(Auth.getUserId());
    const round_number = Number(currentRound);
    const game_id = Number(Auth.getGameId());
    const course_id = Number(Auth.getCourseId());
    fetchTracks({ gameId: game_id, courseId: course_id, userId: user_id, roundNumber: round_number })
      .then(res => {
        const current_page = `${window.location.protocol}//${window.location.host}${url}`;

        const history = res.body.results.filter(i => (
          i.page.startsWith(`${window.location.protocol}//${window.location.host}/round-trivia/`)
        )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/round-trivia/`, ''));
        const trackedPage = res.body.results.find(i => (
          i.page === current_page
        ));

        changeLocation(`${url}?round=${currentRound}`);

        if (!trackedPage) {
          postRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/`)
            .send({
              page: current_page,
              user: user_id,
              course: course_id,
              game: game_id,
              round_number: round_number,
            })
            .then((data) => {
              invalidateTracks();
              this.setState({
                history: [
                  ...history,
                  `${page}${step !== '' ? '/' + step : ''}`,
                ],
              });
            });
        }
      })
      .catch(err => {});
  }

  goNext = e => {
    e.preventDefault();

    const { pages, currentPage, currentStep } = this.state;

    let nextPage = currentPage;
    let nextStep = currentStep + 1;
    if (nextStep > pages[nextPage].steps.length - 1) {
      nextPage = currentPage + 1;
      nextStep = 0;

      if (nextPage > pages.length - 1) {
        return;
      }
    }

    const page = pages[nextPage].alias;
    const step = pages[nextPage].steps[nextStep].alias;

    this.trackPage(
      `/round-trivia/${page}${step !== '' ? '/' + step : ''}`,
      page,
      step,
    );

    this.setState({
      currentPage: nextPage,
      currentStep: nextStep,
      showIncorrectMessage: false,
    });
  }

  goPrev = e => {
    e.preventDefault();

    const { pages, currentPage, currentStep } = this.state;

    let prevPage = currentPage;
    let prevStep = currentStep - 1;
    if (prevStep < 0) {
      prevPage = currentPage - 1;
      if (prevPage < 0) {
        return;
      }

      prevStep = pages[prevPage].steps.length - 1;
    }

    const page = pages[prevPage].alias;
    const step = pages[prevPage].steps[prevStep].alias;

    changeLocation(
      `/round-trivia/${page}${step !== '' ? '/' + step : ''}?round=${this.activeRound}`
    );

    this.setState({
      currentPage: prevPage,
      currentStep: prevStep,
    });
  }

  onValidate = e => {
    e.preventDefault();

    const { validateCallback } = this.state;

    if (validateCallback) {
      validateCallback();
    }
  }

  get isFirstStep() {
    const { currentPage, currentStep } = this.state;

    return currentPage === 0 && currentStep === 0;
  }

  get isLastStep() {
    const { pages, currentPage, currentStep } = this.state;

    return (
      currentPage === pages.length - 1 &&
      currentStep === pages[pages.length - 1].steps.length - 1
    );
  }

  setMode = (mode, validateCallback = null) => {
    if (['basic', 'disabled', 'validate', 'loading'].includes(mode) && mode !== this.state.mode) {
      this.setState({
        mode,
        validateCallback,
      });
    }
  }

  showConfetti = () => {
    this.setState({
      showConfetti: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showConfetti: false,
        })
      }, 5000);
    });
  }

  render() {
    const {
      currentPage,
      currentStep,
      pages,
      mode,
      loading,
      showConfetti,
      isFetchData,
      showIncorrectMessage,
    } = this.state;
    const { rounds } = this.props;
    const currentRound = this.activeRound;
    const ContentPage = loading
      ? null
      : currentPage > -1 && currentStep > -1
      ? pages[currentPage].steps[currentStep].component
      : Page404;
    const data = loading
      ? null
      : currentPage > -1 && currentStep > -1
      ? pages[currentPage].steps[currentStep].data || null
      : null;
    const pageAlias = loading
      ? null
      : currentPage > -1
      ? pages[currentPage].alias
      : null;

    return (
      <Wrapper>
        <Content>
          {ContentPage &&
          <ContentPage
            setMode={this.setMode}
            currentRound={currentRound}
            rounds={rounds}
            data={data}
            stage={`${currentRound}`}
            showConfetti={this.showConfetti}
            setFetchData={this.setFetchData}
            isFetchData={isFetchData}
            showIncorrectMessage={this.showIncorrectMessage}
          />}
          {showConfetti && (
            <ConfettiWrapper>
              <ConfettiPosition>
                <ConfettiExplosion
                  force={0.6}
                  duration={3000}
                  particleCount={100}
                  height={1000}
                  width={1000}
                />
              </ConfettiPosition>
            </ConfettiWrapper>
          )}
          {showIncorrectMessage && <ErrorMessage>This is incorrect!</ErrorMessage>}
          {!ContentPage && (
            <LoaderWrapper>
              <Loader type="ball-scale-ripple" active />
            </LoaderWrapper>
          )}
          {currentPage > -1 && currentStep > -1 &&
          <Pagination
            currentStep={currentStep+1}
            countOfSteps={pages[currentPage].steps.length}
            goNext={this.goNext}
            goPrev={this.goPrev}
            onValidate={this.onValidate}
            isFirstStep={this.isFirstStep}
            isLastStep={this.isLastStep}
            isNextDisabled={mode === 'disabled'}
            isValidationMode={mode === 'validate'}
            isLoadingMode={mode === 'loading'}
            isTriviaPage={pageAlias === 'trivia'}
          />}
        </Content>
      </Wrapper>
    );
  }

}

export default RoundTriviaPage;



// WEBPACK FOOTER //
// ./src/components/onboarding/RoundTriviaPage.js